import React, { useMemo, useState } from "react";
import { Table, Select, Button, Spin } from "antd";
import { useGetUsersQuery } from "../../services/auth";
import { useGetAttendeeHistoryQuery } from "../../services/analytics";
import * as XLSX from "xlsx";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const { Option } = Select;

const AttendeeHistory = () => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const { data: usersData, isFetching: isLoadingUsers } = useGetUsersQuery({
        currentPage: 1,
        resultPerPage: 100, // Fetch top 100 for search or autocomplete
        search: searchTerm,
        userType: "delegate,sponsor,staff"
    });

    const { data: historyData, isFetching: isFetchingHistory } = useGetAttendeeHistoryQuery(
        selectedUsers.join(","),
        { skip: selectedUsers.length === 0 }
    );

    const users = usersData?.users?.data || [];
    const tableData = historyData?.data || [];

    const handleExport = () => {
        if (!tableData || tableData.length === 0) return;
        const exportData = tableData.map(item => ({
            "Event Name": item.eventName,
            "Event Start Date": new Date(item.eventStartDate).toLocaleDateString(),
            "Attendee Status": item.attendeeStatus,
            "Role in Event": item.userRoleInEvent,
            "Day 1": item.attendance?.includes("Day 1") ? "Present" : "Absent",
            "Day 2": item.attendance?.includes("Day 2") ? "Present" : "Absent",
            "Total Meetings": item.meetingStats?.total || 0,
            "Accepted Meetings": item.meetingStats?.accepted || 0,
            "Rejected Meetings": item.meetingStats?.rejected || 0,
            "Pending Meetings": item.meetingStats?.pending || 0
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Attendee History");
        XLSX.writeFile(wb, `Attendee_History_${selectedUsers.join("_")}.xlsx`);
    };

    const columns = [
        { title: "Attendee", dataIndex: "userName", key: "userName" },
        { title: "Event Name", dataIndex: "eventName", key: "eventName" },
        {
            title: "Status",
            dataIndex: "attendeeStatus",
            key: "attendeeStatus",
            render: (v) => <span className="capitalize">{v}</span>,
        },
        {
            title: "Package/Role",
            dataIndex: "userRoleInEvent",
            key: "userRoleInEvent",
            render: (v) => <span className="capitalize">{v || "N/A"}</span>,
        },
        {
            title: "Day 1",
            dataIndex: "attendance",
            key: "day1",
            render: (attendance) =>
                attendance?.includes("Day 1") ? "Present" : "Absent",
        },
        {
            title: "Day 2",
            dataIndex: "attendance",
            key: "day2",
            render: (attendance) =>
                attendance?.includes("Day 2") ? "Present" : "Absent",
        },
        { title: "Total Meetings", dataIndex: ["meetingStats", "total"], key: "total" },
        { title: "Accepted", dataIndex: ["meetingStats", "accepted"], key: "accepted" },
        { title: "Pending", dataIndex: ["meetingStats", "pending"], key: "pending" },
        { title: "Rejected", dataIndex: ["meetingStats", "rejected"], key: "rejected" },
    ];

    // Aggregate comparison data for chart: one bar per attendee
    const comparisonData = useMemo(() => {
        const byUser = new Map();
        tableData.forEach((item) => {
            if (!item.userId) return;
            const key = item.userId;
            const existing = byUser.get(key) || {
                userId: item.userId,
                userName: item.userName || "Unknown",
                total: 0,
                accepted: 0,
                pending: 0,
                rejected: 0,
            };
            const stats = item.meetingStats || {};
            existing.total += stats.total || 0;
            existing.accepted += stats.accepted || 0;
            existing.pending += stats.pending || 0;
            existing.rejected += stats.rejected || 0;
            byUser.set(key, existing);
        });
        return Array.from(byUser.values());
    }, [tableData]);

    return (
        <div className="animate-fade-in">
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center w-full sm:w-auto">
                    <span className="mr-0 sm:mr-4 mb-2 sm:mb-0 font-semibold text-gray-700 whitespace-nowrap">
                        Select Attendees:
                    </span>
                    <Select
                        mode="multiple"
                        showSearch
                        className="w-full sm:w-[400px]"
                        placeholder="Type to search and select attendees"
                        onSearch={(v) => setSearchTerm(v)}
                        onChange={(vals) => setSelectedUsers(vals)}
                        filterOption={false}
                        loading={isLoadingUsers}
                    >
                        {users.map(u => (
                            <Option key={u._id} value={u._id}>
                                {u.first_name} {u.last_name} ({u.email})
                            </Option>
                        ))}
                    </Select>
                </div>
                <Button
                    type="primary"
                    disabled={!tableData || tableData.length === 0}
                    onClick={handleExport}
                    className="bg-primary hover:bg-yellow-600 border-none"
                >
                    Export Data
                </Button>
            </div>

            {isFetchingHistory ? (
                <div className="flex justify-center p-20">
                    <Spin size="large" />
                </div>
            ) : selectedUsers.length > 0 ? (
                tableData.length > 0 ? (
                    <>
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden transition-all duration-300 mb-6">
                            <Table
                                dataSource={tableData}
                                columns={columns}
                                rowKey={(record) =>
                                    `${record.eventId}-${record.userId || record.attendeeId}`
                                }
                                pagination={{ className: "px-4 mb-4" }}
                                rowClassName="hover:bg-gray-50/50 transition-colors"
                            />
                        </div>

                        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6">
                            <h3 className="font-semibold text-lg text-gray-700 mb-4">
                                Comparison by Attendee (Total Meetings)
                            </h3>
                            <div style={{ height: 360 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={comparisonData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <XAxis
                                            dataKey="userName"
                                            tick={{ fill: "#666" }}
                                            axisLine={{ stroke: "#e8e8e8" }}
                                        />
                                        <YAxis
                                            tick={{ fill: "#666" }}
                                            axisLine={{ stroke: "#e8e8e8" }}
                                        />
                                        <Tooltip
                                            cursor={{ fill: "#f5f5f5" }}
                                            contentStyle={{
                                                borderRadius: "8px",
                                                border: "none",
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                            }}
                                        />
                                        <Legend />
                                        <Bar
                                            dataKey="accepted"
                                            name="Accepted"
                                            stackId="a"
                                            fill="#52c41a"
                                        />
                                        <Bar
                                            dataKey="pending"
                                            name="Pending"
                                            stackId="a"
                                            fill="#faad14"
                                        />
                                        <Bar
                                            dataKey="rejected"
                                            name="Rejected"
                                            stackId="a"
                                            fill="#f5222d"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center text-gray-400 mt-12 bg-white p-12 rounded-2xl border border-gray-100 shadow-sm">
                        <p className="text-lg">
                            The selected attendees have not participated in any events yet.
                        </p>
                    </div>
                )
            ) : (
                <div className="text-center text-gray-400 mt-20 flex flex-col items-center">
                    <svg
                        width="64"
                        height="64"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="mb-4 text-gray-300"
                    >
                        <path
                            d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                            fill="currentColor"
                        />
                    </svg>
                    <p className="text-lg">
                        Please search and select one or more attendees to view their history.
                    </p>
                </div>
            )}
        </div>
    );
};

export default AttendeeHistory;
